import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SEOHead, buildFAQLD } from "@/components/seo/SEOHead";
import { 
  ArrowRight, 
  Upload, 
  Zap, 
  Shield, 
  BarChart3, 
  FileText, 
  Database,
  CheckCircle,
  Play,
  Layers,
  AlertTriangle,
  XCircle,
  Clock,
  Brain,
  TrendingUp,
  Users,
  FileSearch,
  Sparkles,
  Target,
  RefreshCw,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import ProductWalkthrough from "@/components/home/ProductWalkthrough";
import { ParticleField, DataStream, FlowLines, NodeNetwork, MorphBlob, OrbitDots } from "@/components/effects/DynamicBackgrounds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const problems = [
  {
    icon: Database,
    title: "Data is Fragmented",
    description: "Spend data scattered across ERPs, spreadsheets, and siloed systems"
  },
  {
    icon: Clock,
    title: "Insights Come Too Late",
    description: "By the time you get analysis, the decision window has closed"
  },
  {
    icon: FileText,
    title: "Contracts Are Reactive",
    description: "Risks discovered after signing, renewals missed, obligations forgotten"
  },
  {
    icon: Users,
    title: "Manual Effort Everywhere",
    description: "Teams spend 80% of time on data prep, 20% on actual decisions"
  }
];

const solutionFlow = [
  { step: "Upload", icon: Upload, color: "#00B4D8", product: "Your Data" },
  { step: "Clean", icon: RefreshCw, color: "#8B5CF6", product: "DataRefinery" },
  { step: "Analyse", icon: BarChart3, color: "#10B981", product: "SpendLens" },
  { step: "Act", icon: Zap, color: "#F59E0B", product: "Insights" },
  { step: "Govern", icon: Shield, color: "#EF4444", product: "ConTrack" }
];

const products = [
  {
    id: "data-refinery",
    name: "DataRefinery",
    icon: Database,
    outcome: "Clean, classified, analysis-ready data",
    speed: "Hours, not weeks",
    aiCapability: "Auto-categorizes 95%+ of transactions",
    color: "#00B4D8"
  },
  {
    id: "spendlens",
    name: "SpendLens",
    icon: BarChart3,
    outcome: "Actionable spend insights with savings identified",
    speed: "Same-day analytics",
    aiCapability: "AI generates action plans and recommendations",
    color: "#10B981"
  },
  {
    id: "contrack",
    name: "ConTrack",
    icon: FileText,
    outcome: "Contract risks flagged, obligations tracked",
    speed: "Analysis in ~20 seconds",
    aiCapability: "Reviews 40+ jurisdictions, generates summaries",
    color: "#8B5CF6"
  }
];

const aiActions = [
  {
    action: "Reads your contracts",
    result: "Flags risks, extracts obligations, recommends actions",
    icon: FileSearch
  },
  {
    action: "Reads your spend data",
    result: "Identifies savings, detects anomalies, benchmarks performance",
    icon: TrendingUp
  },
  {
    action: "Generates action plans",
    result: "Prioritized recommendations with implementation steps",
    icon: Target
  },
  {
    action: "Produces executive summaries",
    result: "Board-ready insights in one click",
    icon: Sparkles
  }
];

const realityFeatures = [
  {
    title: "No IT Required",
    description: "Upload files directly. No integrations, no dependencies.",
    icon: Zap
  },
  {
    title: "No Long Implementation",
    description: "Start using the platform in minutes, not months.",
    icon: Clock
  },
  {
    title: "Same-Day Results",
    description: "Upload in the morning, present insights by afternoon.",
    icon: CheckCircle
  }
];

const comparisonData = [
  { feature: "Setup Time", procureaide: "Minutes", traditional: "Months" },
  { feature: "IT Dependency", procureaide: "None", traditional: "High" },
  { feature: "AI Outputs", procureaide: "Native", traditional: "Add-on" },
  { feature: "Time to Value", procureaide: "Same Day", traditional: "Weeks" }
];

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thank you for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md mx-auto" data-testid="newsletter-form">
      <Input
        type="email"
        placeholder="Enter your work email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="flex-1 bg-[#111F36] border-[#00B4D8]/30 focus:border-[#00B4D8] text-[#F8FAFC] placeholder:text-[#64748B]"
        data-testid="newsletter-email-input"
      />
      <Button 
        type="submit"
        disabled={loading}
        className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] px-6"
        data-testid="newsletter-submit-btn"
      >
        {loading ? "..." : <><Send className="w-4 h-4 mr-2" /> Subscribe</>}
      </Button>
    </form>
  );
};

export const HomePage = () => {
  return (
    <div className="bg-[#020617]">
      <SEOHead
        path="/"
        jsonLd={buildFAQLD([
          { question: "What is ProcureAide?", answer: "ProcureAide is an AI-powered procurement intelligence platform that helps organisations gain spend visibility, manage contracts and unlock savings through three modular products: DataRefinery, SpendLens and ConTrack." },
          { question: "Which countries does ProcureAide operate in?", answer: "ProcureAide currently serves organisations in Pakistan and Canada, with plans for further expansion." },
          { question: "What are ProcureAide's products?", answer: "ProcureAide offers three modular products: DataRefinery for data cleansing and classification, SpendLens for spend analytics and visibility, and ConTrack for contract lifecycle management." },
        ])}
      />
      {/* Hero Section - Rewritten */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-50" />
        <ParticleField count={25} />
        <DataStream count={6} className="opacity-40" />
        <MorphBlob color="rgba(0,180,216,0.06)" size="600px" className="top-[10%] right-[-10%]" />
        <MorphBlob color="rgba(10,36,99,0.12)" size="500px" className="bottom-[-10%] left-[-10%]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.p 
                variants={fadeInUp}
                className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
              >
                AI-Powered Procurement Intelligence Platform
              </motion.p>
              <motion.h1 
                variants={fadeInUp}
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
              >
                Procurement.{" "}
                <span className="text-[#00B4D8] neon-text">Rebuilt</span> with Intelligence.
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-lg text-[#94A3B8] mb-8 max-w-xl"
              >
                Upload your data. Analyse spend. Detect risk. Take action — all in one platform.
              </motion.p>
              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap gap-4"
              >
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="hero-try-demo-btn"
                  onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Play className="mr-2 w-5 h-5" />
                  Preview Platform
                </Button>
              </motion.div>

              {/* Quick Stats */}
              <motion.div 
                variants={fadeInUp}
                className="mt-12 pt-8 border-t border-[#00B4D8]/10 grid grid-cols-3 gap-6"
              >
                <div>
                  <div className="text-2xl font-bold text-[#00B4D8]">&lt;1hr</div>
                  <p className="text-xs text-[#64748B]">Time to First Insight</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#00B4D8]">~20s</div>
                  <p className="text-xs text-[#64748B]">Contract Analysis</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#00B4D8]">Zero</div>
                  <p className="text-xs text-[#64748B]">IT Setup Required</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Platform Preview */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <OrbitDots count={4} radius={140} className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
              <div className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(0,180,216,0.1)] relative z-10">
                <img
                  src="https://customer-assets.emergentagent.com/job_eb677b80-1d6f-4298-b1f5-516bcc846e81/artifacts/4htx5oil_DR2.jpeg"
                  alt="Procureaide DataRefinery Dashboard"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/60 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM Section - NEW */}
      <section className="py-24 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={10} className="opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF4444]/10 border border-[#EF4444]/20 mb-6"
            >
              <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
              <span className="text-sm text-[#EF4444]">The Reality</span>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Procurement is Broken
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#111F36] border border-[#EF4444]/10 rounded-xl p-6 hover:border-[#EF4444]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-[#EF4444]/10 flex items-center justify-center mb-4">
                  <problem.icon className="w-6 h-6 text-[#EF4444]" />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">
                  {problem.title}
                </h3>
                <p className="text-sm text-[#94A3B8]">
                  {problem.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE SOLUTION Section - NEW */}
      <section className="py-24 relative overflow-hidden">
        <FlowLines count={4} className="opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mb-6"
            >
              <CheckCircle className="w-4 h-4 text-[#10B981]" />
              <span className="text-sm text-[#10B981]">The Solution</span>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              One Platform. Complete Intelligence.
            </motion.h2>
          </motion.div>

          {/* Visual Flow */}
          <div className="relative mb-16">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#00B4D8]/20 via-[#00B4D8] to-[#00B4D8]/20 transform -translate-y-1/2" />
            
            <div className="grid md:grid-cols-5 gap-6">
              {solutionFlow.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div 
                    className="relative z-10 w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 border-2"
                    style={{ 
                      backgroundColor: `${step.color}15`,
                      borderColor: step.color,
                      boxShadow: `0 0 20px ${step.color}40`
                    }}
                  >
                    <step.icon className="w-8 h-8" style={{ color: step.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#F8FAFC] mb-1">{step.step}</h3>
                  <p className="text-sm text-[#64748B]">{step.product}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE PLATFORM - Product Cards */}
      <section className="py-24 bg-[#0B1426]/50 relative overflow-hidden">
        <ParticleField count={15} className="opacity-40" />
        <MorphBlob color="rgba(16,185,129,0.04)" size="400px" className="top-[20%] left-[-5%]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              The Platform
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Pick What You Need. Start There.
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Each module is a standalone product — use one, two, or all three. 
              Go for what suits your organisational objectives.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={`/products/${product.id}`}
                  className="block product-card h-full p-8 group"
                  data-testid={`product-card-${product.id}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${product.color}20` }}
                  >
                    <product.icon className="w-7 h-7" style={{ color: product.color }} />
                  </div>
                  <h3 className="text-xl font-bold text-[#F8FAFC] mb-4">
                    {product.name}
                  </h3>
                  
                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Outcome</p>
                      <p className="text-sm text-[#F8FAFC]">{product.outcome}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Speed</p>
                      <p className="text-sm font-medium" style={{ color: product.color }}>{product.speed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">AI Capability</p>
                      <p className="text-sm text-[#94A3B8]">{product.aiCapability}</p>
                    </div>
                  </div>

                  <div className="flex items-center text-[#00B4D8] text-sm font-medium group-hover:gap-3 gap-2 transition-all">
                    See it in action <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT THE AI ACTUALLY DOES - CRITICAL */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00B4D8]/10 border border-[#00B4D8]/20 mb-6"
            >
              <Brain className="w-4 h-4 text-[#00B4D8]" />
              <span className="text-sm text-[#00B4D8]">AI That Works</span>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              What the AI Actually Does
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Not buzzwords. Real actions that save you time and surface insights you'd otherwise miss.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {aiActions.map((item, index) => (
              <motion.div
                key={item.action}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl p-6 flex gap-4"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#00B4D8]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{item.action}</h3>
                  <p className="text-sm text-[#94A3B8]">{item.result}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Walkthrough Section */}
      <section className="py-24 bg-[#0B1426]/50 relative overflow-hidden" id="demo">
        <DataStream count={5} className="opacity-25" />
        <NodeNetwork count={8} className="opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              See It In Action
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Walk Through the Platform
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              See exactly what you get — real screenshots from the live platform. Explore each module at your own pace.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="bg-[#111F36] border border-[#00B4D8]/20 rounded-2xl p-6 sm:p-8 shadow-[0_0_60px_rgba(0,180,216,0.08)]">
              <Tabs defaultValue="data-refinery" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-[#0B1426] p-1 rounded-lg mb-8">
                  <TabsTrigger 
                    value="data-refinery" 
                    className="data-[state=active]:bg-[#00B4D8] data-[state=active]:text-[#020617] rounded-md font-medium text-xs sm:text-sm"
                    data-testid="demo-tab-data-refinery"
                  >
                    <Database className="w-4 h-4 mr-1 sm:mr-2" />
                    DataRefinery
                  </TabsTrigger>
                  <TabsTrigger 
                    value="spendlens"
                    className="data-[state=active]:bg-[#10B981] data-[state=active]:text-[#020617] rounded-md font-medium text-xs sm:text-sm"
                    data-testid="demo-tab-spendlens"
                  >
                    <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
                    SpendLens
                  </TabsTrigger>
                  <TabsTrigger 
                    value="contrack"
                    className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-[#020617] rounded-md font-medium text-xs sm:text-sm"
                    data-testid="demo-tab-contrack"
                  >
                    <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                    ConTrack
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="data-refinery">
                  <ProductWalkthrough productId="data-refinery" />
                </TabsContent>
                <TabsContent value="spendlens">
                  <ProductWalkthrough productId="spendlens" />
                </TabsContent>
                <TabsContent value="contrack">
                  <ProductWalkthrough productId="contrack" />
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BUILT FOR YOUR REALITY */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Built for Your Reality
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Designed for companies without large procurement teams or IT resources.
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {realityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-full bg-[#00B4D8]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-8 h-8 text-[#00B4D8]" />
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{feature.title}</h3>
                <p className="text-[#94A3B8]">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <h3 className="text-xl font-bold text-[#F8FAFC] text-center mb-6">Built Differently</h3>
            <div className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl overflow-hidden">
              <div className="grid grid-cols-3 bg-[#111F36] p-4 text-sm font-semibold">
                <div className="text-[#94A3B8]">Feature</div>
                <div className="text-[#00B4D8] text-center">Procureaide</div>
                <div className="text-[#64748B] text-center">Traditional Tools</div>
              </div>
              {comparisonData.map((row, index) => (
                <div key={index} className="grid grid-cols-3 p-4 border-t border-[#00B4D8]/10">
                  <div className="text-[#F8FAFC]">{row.feature}</div>
                  <div className="text-[#10B981] text-center font-medium">{row.procureaide}</div>
                  <div className="text-[#EF4444]/70 text-center">{row.traditional}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-[#0B1426]/50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Stay Ahead in Procurement
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Get the latest AI procurement insights delivered to your inbox.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <NewsletterForm />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-[#0A2463]/20 to-[#020617]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Ready to Start?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8 max-w-xl mx-auto"
            >
              You get insights in under 1 hour. No IT setup. No long implementation.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="cta-try-demo-btn"
                >
                  Preview Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10 px-8"
                  data-testid="cta-see-action-btn"
                >
                  See It in Action
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
